export const GENERIC_ERROR = "Something went wrong on our side. Please try again in a moment.";
export const SESSION_EXPIRED = "Your session has expired. Please sign in again.";
export const OFFLINE = "We can't reach the server. Check your connection and try again.";

const STATUS_MESSAGES = {
  400: "That request wasn't valid. Please check the details and try again.",
  401: SESSION_EXPIRED,
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for.",
  409: "That conflicts with something that already exists.",
  413: "That file is too large. Please choose a smaller one.",
  429: "Too many attempts. Please wait a moment and try again.",
};

// First match wins. Replacement strings may use $1, $2 from the match.
const RULES = [
  [/^request (is rejected|failed)\.*$/i, "We couldn't complete that request. Please try again."],
  [/^(something went wrong|internal server error|server error)\.*$/i, GENERIC_ERROR],
  [/unauthorized request|jwt|token (is )?(expired|invalid)/i, SESSION_EXPIRED],
  [/^(access denied|forbidden)\.*$/i, STATUS_MESSAGES[403]],
  [/network error|failed to fetch/i, OFFLINE],
  [/timeout|econnaborted/i, "That took too long. Please try again."],
  [/^user (id )?not found\.*$/i, "We couldn't find that user."],
  [/^company not matched\.*$/i, "That company doesn't match this account."],
  [/subscription id missing/i, "Please choose a subscription plan first."],
  [/^iap image required\.*$/i, "Please add the in-app purchase image."],
  [/challenge pack logo required/i, "Please add a logo for this challenge pack."],
  [/challenge pack banner image required/i, "Please add a banner image for this challenge pack."],
  [/challenges array is missing or empty/i, "Please add at least one challenge before saving."],
  [/challenge image (?:is )?required on challenge (\d+)/i, "Challenge $1 needs an image."],
  [/^please select type\.*$/i, "Please choose a type."],
  [/^please select users\.*$/i, "Please choose at least one user."],
  [/^please add time\.*$/i, "Please set a time."],
  [/^please upload an image\.*$/i, "Please upload an image."],
  [/upload succeeded but no image url/i, "The image uploaded but couldn't be attached. Please try again."],
  [/images file must be a \.zip archive/i, "Please choose a .zip file for the images."],
  [/^please fill in (.+?)\.*$/i, "Please enter a value for $1."],
  [/^failed to (\w+) (.+?)\.*$/i, "We couldn't $1 $2. Please try again."],
];

const TECHNICAL = /sequelize|\bER_[A-Z_]+|\bSQL\b|\bat .+:\d+|\[object |^undefined$|^null$|<\/?html|^\s*[{[]/i;

function polish(text) {
  let s = text.replace(/\s+/g, " ").trim().replace(/\.{2,}$/, ".");
  if (!s) return s;
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** Turn any raw, technical or terse message into plain, polite text. */
export function friendlyMessage(raw, kind = "error") {
  const fallback = kind === "success" ? "Done." : GENERIC_ERROR;
  const text = typeof raw === "string" ? raw : raw?.message;
  if (typeof text !== "string" || !text.trim()) return fallback;

  const statusHit = text.match(/status code (\d{3})/i);
  if (statusHit) {
    const code = Number(statusHit[1]);
    return STATUS_MESSAGES[code] || (code >= 500 ? GENERIC_ERROR : STATUS_MESSAGES[400]);
  }
  if (kind === "success") return polish(text);

  if (TECHNICAL.test(text) || text.length > 220) return GENERIC_ERROR;
  for (const [pattern, reply] of RULES) {
    if (pattern.test(text)) return polish(text.replace(pattern, reply));
  }
  return polish(text);
}
